import { buildHighlightGeometryDiagnostics } from '@/shared/lib/highlighter/highlightGeometryDiagnostics';

// Ported from the prescription-highlighter PoC. Only the geometry pipeline
// (buildAnalysisRows + helpers) is kept; the PoC's server-side OCR-call and
// post-process helpers (sharp / node-fetch / form-data) are dropped because the
// admin flow supplies the Document AI `document` and the CONFIRMED drug list.
const ROW_THRESHOLD = 0.009;
const CODE_ANCHOR_MAX_X = 0.36;
const DATA_ROW_TOP_CUTOFF = 0.05;
const DATA_ROW_BOTTOM_CUTOFF = 0.92;
const MAX_ROW_SLOPE = 0.08;
const MIN_SLOPE_TO_CHECK = 0.003;
const ROW_SLOPE_AGREE_TOL = 0.005;
const ROW_BAND_MIN_EDGE_PADDING = 0.0008;
const ROW_BAND_EDGE_PADDING_RATIO = 0.1;
const ROW_BAND_MIN_HEIGHT_RATIO = 1.1;
const ROW_BAND_MIN_PITCH_RATIO = 0.58;
const ROW_BAND_MAX_PITCH_RATIO = 0.86;
const SUBLINE_TOLERANCE_RATIO = 1.0;
const OUTLIER_INTERCEPT_HEIGHT_RATIO = 1.0;
const OUTLIER_INTERCEPT_PITCH_RATIO = 0.45;
const RIGHT_EDGE_MIN_X = 0.72;
// Few-row documents get no global skew fit, so a per-row slope from tokens that
// span only a narrow x-range is noise (a ~1px y-jitter over a short baseline
// reads as a steep tilt — 아이큐어 00000238). Trust per-row slope only when the
// row spans at least this width; otherwise clamp toward flat.
const SHORT_SPAN_X_THRESHOLD = 0.45;
const SHORT_SPAN_MAX_SLOPE = 0.02;

export function normalizeYearMonth(value) {
  const raw = String(value ?? '').trim();
  return /^\d{6}$/.test(raw) ? raw : null;
}

// ── 4점 vertices 정렬: [TL, TR, BR, BL] 순서로 ──────────────────────────
function orderVertices(nv) {
  if (!nv || nv.length < 4) {
    // 4점 미만이면 min/max로 직사각형 생성
    const xs = (nv || []).map((v) => v.x ?? 0);
    const ys = (nv || []).map((v) => v.y ?? 0);
    const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
    const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
    return [
      { x: x0, y: y0 },
      { x: x1, y: y0 },
      { x: x1, y: y1 },
      { x: x0, y: y1 },
    ];
  }
  // Document AI는 일반적으로 [TL, TR, BR, BL] 순서로 반환
  // 중심점 기준으로 정렬해서 보정
  const cx = nv.reduce((s, v) => s + (v.x ?? 0), 0) / nv.length;
  const cy = nv.reduce((s, v) => s + (v.y ?? 0), 0) / nv.length;
  const sorted = [...nv].sort((a, b) => {
    const angleA = Math.atan2((a.y ?? 0) - cy, (a.x ?? 0) - cx);
    const angleB = Math.atan2((b.y ?? 0) - cy, (b.x ?? 0) - cx);
    return angleA - angleB;
  });
  // 시계방향 → TL(좌상), TR(우상), BR(우하), BL(좌하) 재매핑
  // atan2 기준: TL은 3사분면(-π~-π/2), TR은 4사분면(-π/2~0)...
  // 가장 작은 (x+y)가 TL
  const withSum = sorted.map((v) => ({ v, sum: (v.x ?? 0) + (v.y ?? 0) }));
  withSum.sort((a, b) => a.sum - b.sum);
  const tl = withSum[0].v;
  const br = withSum[withSum.length - 1].v;
  const remaining = withSum.slice(1, -1).map((w) => w.v);
  const tr = remaining[0].x > remaining[1].x ? remaining[0] : remaining[1];
  const bl = remaining[0].x > remaining[1].x ? remaining[1] : remaining[0];
  return [tl, tr, br, bl];
}

// ── 토큰 그루핑: Y좌표 기준 row 클러스터 (실제 4점 vertices 보존) ────────
function groupTokensByRow(tokens, fullText) {
  const rows = [];

  for (const token of tokens) {
    const nv = token.layout?.boundingPoly?.normalizedVertices;
    if (!nv || nv.length < 2) continue;

    const seg = token.layout?.textAnchor?.textSegments?.[0] ?? {};
    const start = parseInt(seg.startIndex ?? 0, 10);
    const end = parseInt(seg.endIndex ?? 0, 10);
    const text = fullText.slice(start, end).replace(/\n/g, ' ').trim();
    if (!text) continue;

    const ordered = orderVertices(nv); // [TL, TR, BR, BL]
    const ys = ordered.map((v) => v.y ?? 0);
    const xs = ordered.map((v) => v.x ?? 0);
    const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const tokenInfo = {
      ordered,
      minX,
      maxX,
      minY,
      maxY,
      centerX,
      centerY,
      text,
    };

    const existing = rows.find(
      (r) => Math.abs(r.centerY - centerY) <= ROW_THRESHOLD,
    );
    if (existing) {
      // 실제 4점 vertices를 저장해서 기울기 정보 유지
      existing.tokenVertices.push(tokenInfo);
      existing.minX = Math.min(existing.minX, minX);
      existing.maxX = Math.max(existing.maxX, maxX);
      existing.minY = Math.min(existing.minY, minY);
      existing.maxY = Math.max(existing.maxY, maxY);
      existing.text += ' ' + text;
      existing.centerY = (existing.minY + existing.maxY) / 2;
    } else {
      rows.push({
        centerY,
        minX,
        maxX,
        minY,
        maxY,
        text,
        tokenVertices: [tokenInfo],
      });
    }
  }

  return rows.sort((a, b) => a.centerY - b.centerY);
}

// ── 처방전 데이터 row만 추출 (헤더/합계/여백 제외) ─────────────────────
// 두 가지 문서 유형 모두 지원:
//   (A) 화면 사진: 8~9자리 숫자 코드 2개 이상 (698500220 등)
//   (B) 클린 PDF: 영문+숫자 약품코드 (DTENOF03, DAMLO.FIM1 등) + 한글 약품명 + 숫자
const NUMERIC_CODE_RE = /\d{8,9}/g;
const HAS_DRUG_CODE_RE = /\d{8,9}/; // 비-global: test() 안전
const ALPHANUM_CODE_RE = /\b[A-Z][A-Z0-9]{3,10}\b/g; // 대문자로 시작하는 영숫자 코드
const KOREAN_RE = /[가-힣]/;
// 컬럼 헤더 행에만 나타나는 패턴 (데이터 행에는 없는 것만)
// 주의: '급여', '단가', '원내', '원외'는 데이터 행에도 나오므로 제외
const HEADER_KEYWORDS =
  /^(제약\s*회사|약품\s*코드|약품\s*명|총\s*투여\s*량|수가\s*코드|청구\s*코드)[\s\S]{0,80}(단가|총금액|건수)/;

function extractDrugCodes(text) {
  if (!text) return [];
  return [
    ...new Set([
      ...(text.match(NUMERIC_CODE_RE) ?? []),
      ...(text.match(ALPHANUM_CODE_RE) ?? []),
    ]),
  ];
}

function median(values, fallback = 0) {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!sorted.length) return fallback;
  return sorted[Math.floor(sorted.length / 2)];
}

function percentile(values, ratio, fallback = 0) {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!sorted.length) return fallback;
  const index = Math.max(
    0,
    Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio)),
  );
  return sorted[index];
}

function upperPercentile(values, ratio, fallback = 0) {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!sorted.length) return fallback;
  const index = Math.max(
    0,
    Math.min(sorted.length - 1, Math.ceil((sorted.length - 1) * ratio)),
  );
  return sorted[index];
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function uniqueByReference(items) {
  return [...new Set(items)];
}

function tokenCenterX(token) {
  return token.centerX ?? (token.minX + token.maxX) / 2;
}

function tokenCenterY(token) {
  return token.centerY ?? (token.minY + token.maxY) / 2;
}

function tokenHeight(token) {
  return token.maxY - token.minY;
}

function isNumericDrugCode(value) {
  return /^\d{8,9}$/.test(String(value ?? '').trim());
}

function isCodeAnchorToken(token) {
  if (!token || tokenCenterX(token) > CODE_ANCHOR_MAX_X) return false;
  return extractDrugCodes(token.text).some(isNumericDrugCode);
}

function estimateTokenTextSlope(tokens, fallback = 0) {
  const slopes = tokens
    .map((token) => {
      const topLeft = token.ordered?.[0];
      const topRight = token.ordered?.[1];
      const dx = (topRight?.x ?? 0) - (topLeft?.x ?? 0);
      if (Math.abs(dx) < 0.003) return null;
      const slope = ((topRight?.y ?? 0) - (topLeft?.y ?? 0)) / dx;
      return Math.abs(slope) <= 0.25 ? slope : null;
    })
    .filter((value) => value !== null);
  return median(slopes, fallback);
}

// Least-squares slope of token CENTER y over center x across the whole row.
// Captures the actual left→right baseline tilt (code column → money column),
// unlike estimateTokenTextSlope which only measures each token's internal slant.
function regressionSlopeThroughCenters(tokens) {
  const cx = [];
  const cy = [];
  (tokens ?? []).forEach((token) => {
    const x = tokenCenterX(token);
    const y = tokenCenterY(token);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      cx.push(x);
      cy.push(y);
    }
  });
  if (cx.length < 4) return NaN;
  const mx = cx.reduce((sum, value) => sum + value, 0) / cx.length;
  const my = cy.reduce((sum, value) => sum + value, 0) / cy.length;
  let num = 0;
  let den = 0;
  for (let i = 0; i < cx.length; i += 1) {
    num += (cx[i] - mx) * (cy[i] - my);
    den += (cx[i] - mx) ** 2;
  }
  return den > 1e-9 ? num / den : NaN;
}

// Slope of the chord between the leftmost and rightmost token centers — a second,
// outlier-sensitive baseline estimate used to cross-check the regression.
function chordSlopeThroughCenters(tokens) {
  const usable = (tokens ?? []).filter(
    (token) =>
      Number.isFinite(tokenCenterX(token)) &&
      Number.isFinite(tokenCenterY(token)),
  );
  if (usable.length < 2) return NaN;
  let left = usable[0];
  let right = usable[0];
  usable.forEach((token) => {
    if (tokenCenterX(token) < tokenCenterX(left)) left = token;
    if (tokenCenterX(token) > tokenCenterX(right)) right = token;
  });
  const dx = tokenCenterX(right) - tokenCenterX(left);
  if (Math.abs(dx) < 0.05) return NaN;
  return (tokenCenterY(right) - tokenCenterY(left)) / dx;
}

// Pick the row baseline slope: average regG+chordG when they agree (clean
// baseline), else regG → chordG → per-token slant → fallback. Clamped.
function pickBaselineSlope(tokens, fallbackSlope) {
  const regG = regressionSlopeThroughCenters(tokens);
  const chordG = chordSlopeThroughCenters(tokens);
  const perTok = estimateTokenTextSlope(tokens, null);
  let slope;
  if (
    Number.isFinite(regG) &&
    Number.isFinite(chordG) &&
    Math.abs(regG - chordG) <= ROW_SLOPE_AGREE_TOL
  ) {
    slope = (regG + chordG) / 2;
  } else if (Number.isFinite(regG)) {
    slope = regG;
  } else if (Number.isFinite(chordG)) {
    slope = chordG;
  } else if (
    Number.isFinite(perTok) &&
    Math.abs(perTok) >= MIN_SLOPE_TO_CHECK
  ) {
    slope = perTok;
  } else {
    slope = fallbackSlope ?? 0;
  }
  return clamp(slope, -MAX_ROW_SLOPE, MAX_ROW_SLOPE);
}

// Robust linear fit of the page skew as a function of vertical position:
// skew(cy) = a + b * cy. A photographed/curled prescription does NOT have a
// single constant tilt — measured ground truth on 00000013.jpg shows the row
// slope decaying smoothly from ~0.033 at the top to ~0 at the bottom. Per-row
// slope estimates are noisy and collapse (or spike) on some rows, which made
// bands run flat and cover the neighbouring row's money column. The whole page
// shares ONE smooth skew trend, so we fit it globally from all rows and reject
// the per-row outliers via IRLS (Huber) weighting. The ~50 well-behaved rows
// dominate the fit; the handful of collapsed/spiked rows get down-weighted.
function fitGlobalSkew(samples) {
  const usable = samples.filter(
    (sample) => Number.isFinite(sample.cy) && Number.isFinite(sample.slope),
  );
  if (usable.length < 6) return null;
  const cyValues = usable.map((sample) => sample.cy);
  if (Math.max(...cyValues) - Math.min(...cyValues) < 0.1) return null;

  let weights = usable.map((sample) => Math.max(0.1, sample.weight ?? 1));
  let model = null;
  for (let iteration = 0; iteration < 12; iteration += 1) {
    let sw = 0;
    let swx = 0;
    let swy = 0;
    let swxx = 0;
    let swxy = 0;
    usable.forEach((sample, index) => {
      const w = weights[index];
      sw += w;
      swx += w * sample.cy;
      swy += w * sample.slope;
      swxx += w * sample.cy * sample.cy;
      swxy += w * sample.cy * sample.slope;
    });
    const det = sw * swxx - swx * swx;
    if (Math.abs(det) < 1e-9) return model;
    const b = (sw * swxy - swx * swy) / det;
    const a = (swy - b * swx) / sw;
    model = { a, b };

    const residuals = usable.map((sample) =>
      Math.abs(sample.slope - (a + b * sample.cy)),
    );
    const medianResidual = median(residuals, 0.001);
    const cutoff = 1.345 * Math.max(medianResidual, 0.001);
    weights = usable.map((sample, index) => {
      const base = Math.max(0.1, sample.weight ?? 1);
      const residual = residuals[index];
      return residual <= cutoff ? base : base * (cutoff / residual);
    });
  }
  return model;
}

// Drop right-column tokens that actually belong to a vertically-adjacent row.
// Far from the left code anchor, the money columns (총용량/총금액) of neighbouring
// tilted rows sit close together, so nearest-row assignment sometimes grabs the
// neighbour's amount tokens — which makes the band cover the wrong row's money.
// Keep a right token only if its baseline intercept matches the row's reliably
// assigned left/centre tokens (code/name/company columns).
function rejectForeignRightTokens(tokens, slope, localPitch, anchor) {
  const refTokens = (tokens ?? []).filter(
    (token) => tokenCenterX(token) <= RIGHT_EDGE_MIN_X,
  );
  if (refTokens.length < 2) return tokens;
  const refIntercept = median(
    refTokens
      .map((token) => tokenCenterY(token) - slope * tokenCenterX(token))
      .filter(Number.isFinite),
    (anchor?.centerY ?? 0) - slope * (anchor?.centerX ?? 0),
  );
  const tolerance = Math.max(0.005, localPitch * 0.5);
  const cleaned = tokens.filter((token) => {
    if (tokenCenterX(token) <= RIGHT_EDGE_MIN_X) return true;
    const intercept = tokenCenterY(token) - slope * tokenCenterX(token);
    return Math.abs(intercept - refIntercept) <= tolerance;
  });
  // Never strip the entire right side; if filtering removed every right token,
  // keep the originals (better an imperfect anchor than none).
  const keptRight = cleaned.some(
    (token) => tokenCenterX(token) > RIGHT_EDGE_MIN_X,
  );
  const hadRight = tokens.some(
    (token) => tokenCenterX(token) > RIGHT_EDGE_MIN_X,
  );
  return hadRight && !keptRight ? tokens : cleaned;
}

function estimateTokenCenterLine(tokens, fallbackAnchor, fallbackSlope) {
  const usableTokens = tokens?.length
    ? tokens
    : (fallbackAnchor?.codeTokens ?? []);
  const tokenSlope = estimateTokenTextSlope(usableTokens, null);
  const slope = clamp(
    Number.isFinite(tokenSlope) ? tokenSlope : fallbackSlope || 0,
    -MAX_ROW_SLOPE,
    MAX_ROW_SLOPE,
  );
  const centerXs = usableTokens.map(tokenCenterX).filter(Number.isFinite);
  const intercepts = usableTokens
    .map((token) => tokenCenterY(token) - slope * tokenCenterX(token))
    .filter(Number.isFinite);
  const centerX = median(centerXs, fallbackAnchor?.centerX ?? 0);
  const intercept = median(
    intercepts,
    (fallbackAnchor?.centerY ?? 0) -
      slope * (fallbackAnchor?.centerX ?? centerX),
  );

  return {
    centerX,
    centerY: intercept + slope * centerX,
    slope,
  };
}

function tokenEdgeIntercepts(tokens, slope, edge) {
  const vertexIndexes = edge === 'top' ? [0, 1] : [3, 2];
  return tokens
    .flatMap((token) =>
      vertexIndexes
        .map((vertexIndex) => token.ordered?.[vertexIndex])
        .filter(Boolean),
    )
    .map((vertex) => vertex.y - slope * vertex.x)
    .filter(Number.isFinite);
}

function getRightEdgeTokens(tokens) {
  const sortedCenters = tokens
    .map(tokenCenterX)
    .filter(Number.isFinite)
    .sort((left, right) => left - right);
  if (!sortedCenters.length) return [];

  const quantileIndex = Math.max(
    0,
    Math.floor((sortedCenters.length - 1) * 0.7),
  );
  const threshold = Math.max(
    RIGHT_EDGE_MIN_X,
    sortedCenters[quantileIndex] - 0.015,
  );
  return tokens.filter((token) => tokenCenterX(token) >= threshold);
}

function edgeConstraintWindow(edgeIntercepts, edge, allowance, missAllowance) {
  if (!edgeIntercepts?.length) return null;
  const minEdge = Math.min(...edgeIntercepts);
  const maxEdge = Math.max(...edgeIntercepts);

  if (edge === 'top') {
    return {
      lower: maxEdge - allowance,
      upper: minEdge + missAllowance,
      midpoint: (minEdge + maxEdge) / 2,
    };
  }

  return {
    lower: maxEdge - missAllowance,
    upper: minEdge + allowance,
    midpoint: (minEdge + maxEdge) / 2,
  };
}

function intersectWindows(windows) {
  const usable = windows.filter(Boolean);
  if (!usable.length) return null;
  return {
    lower: Math.max(...usable.map((window) => window.lower)),
    upper: Math.min(...usable.map((window) => window.upper)),
    midpoint: median(usable.map((window) => window.midpoint)),
  };
}

function chooseWindowIntercept(window) {
  if (!window) return null;
  if (window.lower <= window.upper) return (window.lower + window.upper) / 2;
  return (window.lower + window.upper) / 2;
}

// Snap a band edge intercept into its coverage window. The window upper bound
// caps over-coverage (excess) and the lower bound guarantees the edge reaches
// the glyphs (miss). A row that already passes sits inside the window, so this
// is a no-op for it; a feasible-but-mis-clamped row gets pulled back into the
// satisfying range; an infeasible window (lower > upper) collapses to the
// midpoint, which minimizes the worst-case violation.
function clampInterceptToWindow(value, window) {
  if (
    !window ||
    !Number.isFinite(window.lower) ||
    !Number.isFinite(window.upper)
  ) {
    return value;
  }
  if (window.lower > window.upper) return (window.lower + window.upper) / 2;
  return Math.min(Math.max(value, window.lower), window.upper);
}

function localAnchorPitch(anchors, index, fallback) {
  const values = [];
  const current = anchors[index];
  const previous = anchors[index - 1];
  const next = anchors[index + 1];
  if (previous) values.push(Math.abs(current.centerY - previous.centerY));
  if (next) values.push(Math.abs(next.centerY - current.centerY));
  return median(values, fallback);
}

function filterSameSublineTokens(rowTokens, anchor, fallbackMedianHeight) {
  if (!rowTokens?.length) return [];

  const heights = rowTokens.map(tokenHeight).filter(Number.isFinite);
  const medianHeight = median(heights, fallbackMedianHeight);
  const slope = anchor?.slope ?? 0;
  const referenceToken =
    anchor?.codeTokens?.[0] ??
    rowTokens.reduce((left, token) => (left.minX <= token.minX ? left : token));
  const referenceIntercept =
    tokenCenterY(referenceToken) - slope * tokenCenterX(referenceToken);
  const tolerance = medianHeight * SUBLINE_TOLERANCE_RATIO;
  const sameLine = rowTokens.filter((token) => {
    const intercept = tokenCenterY(token) - slope * tokenCenterX(token);
    return Math.abs(intercept - referenceIntercept) <= tolerance;
  });

  return sameLine.length >= 2 ? sameLine : rowTokens;
}

function filterVerticalOutlierTokens(
  rowTokens,
  anchor,
  localPitch,
  fallbackMedianHeight,
) {
  if (!rowTokens?.length || rowTokens.length < 5) return rowTokens ?? [];

  const heights = rowTokens.map(tokenHeight).filter(Number.isFinite);
  const medianHeight = median(heights, fallbackMedianHeight);
  const slope = anchor?.slope ?? 0;
  const intercepts = rowTokens
    .map((token) => tokenCenterY(token) - slope * tokenCenterX(token))
    .filter(Number.isFinite);
  const center = median(intercepts, null);
  if (!Number.isFinite(center)) return rowTokens;

  const threshold = Math.max(
    medianHeight * OUTLIER_INTERCEPT_HEIGHT_RATIO,
    localPitch * OUTLIER_INTERCEPT_PITCH_RATIO,
  );
  const filtered = rowTokens.filter((token) => {
    const intercept = tokenCenterY(token) - slope * tokenCenterX(token);
    return Math.abs(intercept - center) <= threshold;
  });
  const removedCount = rowTokens.length - filtered.length;
  if (!removedCount || filtered.length < 4 || removedCount > 2)
    return rowTokens;

  const beforeRight = getRightEdgeTokens(rowTokens).length;
  const afterRight = getRightEdgeTokens(filtered).length;
  if (beforeRight && afterRight < Math.max(2, Math.floor(beforeRight * 0.7))) {
    return rowTokens;
  }

  const beforeMinX = Math.min(...rowTokens.map((token) => token.minX));
  const beforeMaxX = Math.max(...rowTokens.map((token) => token.maxX));
  const afterMinX = Math.min(...filtered.map((token) => token.minX));
  const afterMaxX = Math.max(...filtered.map((token) => token.maxX));
  const beforeWidth = beforeMaxX - beforeMinX;
  const afterWidth = afterMaxX - afterMinX;
  if (beforeWidth > 0 && afterWidth < beforeWidth * 0.65) return rowTokens;

  return filtered;
}

function makeTokenEnvelopeVertices({
  rowTokens,
  anchor,
  tableLeft,
  tableRight,
  localPitch,
  fallbackMedianHeight,
}) {
  if (!rowTokens?.length) return null;

  const slope = anchor.slope ?? 0;
  const tokenHeights = rowTokens.map(tokenHeight).filter(Number.isFinite);
  const medianHeight = median(tokenHeights, fallbackMedianHeight);
  const centerIntercept = anchor.centerY - slope * anchor.centerX;
  const fallbackTop = centerIntercept - medianHeight / 2;
  const fallbackBottom = centerIntercept + medianHeight / 2;

  const topIntercepts = tokenEdgeIntercepts(rowTokens, slope, 'top');
  const bottomIntercepts = tokenEdgeIntercepts(rowTokens, slope, 'bottom');
  if (!topIntercepts.length || !bottomIntercepts.length) return null;

  const minTopIntercept = Math.min(...topIntercepts);
  const maxTopIntercept = Math.max(...topIntercepts);
  const minBottomIntercept = Math.min(...bottomIntercepts);
  const maxBottomIntercept = Math.max(...bottomIntercepts);
  const padding = Math.max(
    ROW_BAND_MIN_EDGE_PADDING,
    medianHeight * ROW_BAND_EDGE_PADDING_RATIO,
  );
  const tokenCoverageAllowance = Math.max(0.006, medianHeight * 0.85);
  const tokenMissAllowance = Math.max(0.004, medianHeight * 0.55);
  const rightCoverageAllowance = Math.max(0.005, medianHeight * 0.65);
  const rightMissAllowance = Math.max(0.0035, medianHeight * 0.45);
  const rightTokens = getRightEdgeTokens(rowTokens);
  const rightTopIntercepts = tokenEdgeIntercepts(rightTokens, slope, 'top');
  const rightBottomIntercepts = tokenEdgeIntercepts(
    rightTokens,
    slope,
    'bottom',
  );
  const tokenTopWindow = edgeConstraintWindow(
    topIntercepts,
    'top',
    tokenCoverageAllowance,
    tokenMissAllowance,
  );
  const tokenBottomWindow = edgeConstraintWindow(
    bottomIntercepts,
    'bottom',
    tokenCoverageAllowance,
    tokenMissAllowance,
  );
  const rightTopWindow = edgeConstraintWindow(
    rightTopIntercepts,
    'top',
    rightCoverageAllowance,
    rightMissAllowance,
  );
  const rightBottomWindow = edgeConstraintWindow(
    rightBottomIntercepts,
    'bottom',
    rightCoverageAllowance,
    rightMissAllowance,
  );
  const topWindow = intersectWindows([tokenTopWindow, rightTopWindow]);
  const bottomWindow = intersectWindows([tokenBottomWindow, rightBottomWindow]);
  const rawTopIntercept =
    chooseWindowIntercept(topWindow) ?? (minTopIntercept + maxTopIntercept) / 2;
  const rawBottomIntercept =
    chooseWindowIntercept(bottomWindow) ??
    (minBottomIntercept + maxBottomIntercept) / 2;
  let topIntercept = rawTopIntercept;
  let bottomIntercept = rawBottomIntercept;

  if (
    !Number.isFinite(topIntercept) ||
    !Number.isFinite(bottomIntercept) ||
    bottomIntercept <= topIntercept
  ) {
    topIntercept = fallbackTop - padding;
    bottomIntercept = fallbackBottom + padding;
  }

  const minHeight = Math.max(
    medianHeight * ROW_BAND_MIN_HEIGHT_RATIO,
    localPitch * ROW_BAND_MIN_PITCH_RATIO,
  );
  const maxHeight = Math.max(minHeight, localPitch * ROW_BAND_MAX_PITCH_RATIO);
  let bandHeight = bottomIntercept - topIntercept;
  let bandCenter = (topIntercept + bottomIntercept) / 2;
  const rightMaxHeight =
    rightTopWindow && rightBottomWindow
      ? Math.max(
          bandHeight,
          2 *
            Math.max(
              0,
              Math.min(
                bandCenter - rightTopWindow.lower,
                rightBottomWindow.upper - bandCenter,
              ),
            ),
        )
      : null;
  const effectiveMinHeight = Number.isFinite(rightMaxHeight)
    ? Math.min(minHeight, rightMaxHeight)
    : minHeight;

  if (bandHeight < effectiveMinHeight) {
    bandHeight = effectiveMinHeight;
  } else if (bandHeight > maxHeight) {
    bandHeight = maxHeight;
  }

  topIntercept = bandCenter - bandHeight / 2;
  bottomIntercept = bandCenter + bandHeight / 2;

  // The height clamp + rigid recenter above can push a band edge out of its
  // joint (token + right-edge) coverage window, producing token/right miss or
  // excess. Snap each edge back into its window so the final band satisfies both
  // the glyph-cover (lower) and over-coverage (upper) limits at once. Passing
  // rows already sit inside their windows, so this does not move them.
  topIntercept = clampInterceptToWindow(topIntercept, topWindow);
  bottomIntercept = clampInterceptToWindow(bottomIntercept, bottomWindow);

  // Recenter the band on the reliable text baseline (token CENTERS), preserving
  // the height computed above. The edge-coverage windows derive the center from
  // token top/bottom edges intersected with the right-edge window; on a curled
  // page the right (money) column lifts ~half a row off the baseline, which
  // dragged the band center up (경동/유영/삼진 ↑) or down (글락소 ↓) by ~half a
  // pitch. The token-center line is the true printed row, so anchor to it. Passing
  // (uncurled) rows already have center ≈ baseline, so this is a no-op for them.
  const centerBaselineIntercept = median(
    rowTokens
      .map((token) => tokenCenterY(token) - slope * tokenCenterX(token))
      .filter(Number.isFinite),
    (topIntercept + bottomIntercept) / 2,
  );
  const halfBandHeight = (bottomIntercept - topIntercept) / 2;
  topIntercept = centerBaselineIntercept - halfBandHeight;
  bottomIntercept = centerBaselineIntercept + halfBandHeight;

  const topAt = (x) => clamp(slope * x + topIntercept, 0, 1);
  const bottomAt = (x) => clamp(slope * x + bottomIntercept, 0, 1);

  return [
    { x: tableLeft, y: topAt(tableLeft) },
    { x: tableRight, y: topAt(tableRight) },
    { x: tableRight, y: bottomAt(tableRight) },
    { x: tableLeft, y: bottomAt(tableLeft) },
  ];
}

function clusterCodeAnchorTokens(codeTokens, rowTolerance) {
  const clusters = [];
  const sorted = [...codeTokens].sort(
    (left, right) => tokenCenterY(left) - tokenCenterY(right),
  );

  sorted.forEach((token) => {
    const centerY = tokenCenterY(token);
    const cluster = clusters.find(
      (candidate) => Math.abs(candidate.centerY - centerY) <= rowTolerance,
    );

    if (cluster) {
      cluster.tokens.push(token);
      cluster.centerY = median(
        cluster.tokens.map(tokenCenterY),
        cluster.centerY,
      );
      return;
    }

    clusters.push({ centerY, tokens: [token] });
  });

  return clusters;
}

function estimateCodeAnchorSlope(clusters, fallbackSlope) {
  const slopes = clusters
    .map((cluster) => {
      const sortedByX = [...cluster.tokens].sort(
        (left, right) => tokenCenterX(left) - tokenCenterX(right),
      );
      const left = sortedByX[0];
      const right = sortedByX[sortedByX.length - 1];
      const dx = tokenCenterX(right) - tokenCenterX(left);
      if (!left || !right || dx < 0.03) return null;
      const slope = (tokenCenterY(right) - tokenCenterY(left)) / dx;
      return Math.abs(slope) <= 0.25 ? slope : null;
    })
    .filter((value) => value !== null);

  return median(slopes, fallbackSlope);
}

function makeBoundaryVertices({
  anchors,
  index,
  tableLeft,
  tableRight,
  medianPitch,
}) {
  const row = anchors[index];
  const rowSlope = row.slope ?? 0;
  const lineY = (anchor, x) => anchor.centerY + rowSlope * (x - anchor.centerX);
  const lineGap = (other, current, x) =>
    Math.abs(lineY(other, x) - lineY(current, x));
  const fallbackGapAt = (x) => {
    const previous = anchors[index - 1];
    const next = anchors[index + 1];
    if (previous) return lineGap(previous, row, x);
    if (next) return lineGap(next, row, x);
    return medianPitch;
  };

  const boundaryAt = (x, side) => {
    const currentY = lineY(row, x);
    if (side === 'top') {
      const previous = anchors[index - 1];
      return previous
        ? (lineY(previous, x) + currentY) / 2
        : currentY - fallbackGapAt(x) / 2;
    }

    const next = anchors[index + 1];
    return next
      ? (currentY + lineY(next, x)) / 2
      : currentY + fallbackGapAt(x) / 2;
  };

  return [
    { x: tableLeft, y: boundaryAt(tableLeft, 'top') },
    { x: tableRight, y: boundaryAt(tableRight, 'top') },
    { x: tableRight, y: boundaryAt(tableRight, 'bottom') },
    { x: tableLeft, y: boundaryAt(tableLeft, 'bottom') },
  ];
}

function buildCodeAnchoredDataRows(tokenRows, validCodes) {
  const tokens = tokenRows.flatMap((row) => row.tokenVertices ?? []);
  if (!tokens.length) return [];

  const allCodeTokens = tokens.filter(isCodeAnchorToken).filter((token) => {
    const centerY = tokenCenterY(token);
    return centerY >= DATA_ROW_TOP_CUTOFF && centerY <= DATA_ROW_BOTTOM_CUTOFF;
  });

  // Prefer anchors whose code matches a Gemini-extracted drug code. This rejects
  // spurious 8-9 digit tokens that aren't drugs — e.g. a Word/print ruler read as
  // "12345678910" (아이큐어 00000238), which otherwise anchors a phantom row at
  // the page top and skews every band into a diagonal. Fall back to all numeric
  // code tokens when the Gemini codes don't match (OCR misreads / empty codes),
  // so the 00000013-type path is unchanged.
  const codeMatches = (token) =>
    extractDrugCodes(token.text).some((code) => validCodes.has(code));
  const validatedCodeTokens =
    validCodes && validCodes.size ? allCodeTokens.filter(codeMatches) : [];
  const codeTokens =
    validatedCodeTokens.length >= 2 ? validatedCodeTokens : allCodeTokens;

  if (codeTokens.length < 2) return [];

  const medianTokenHeight = median(tokens.map(tokenHeight), 0.01);
  const rowTolerance = Math.max(0.0035, medianTokenHeight * 0.7);
  const tokenSlope = estimateTokenTextSlope(tokens);
  const clusters = clusterCodeAnchorTokens(codeTokens, rowTolerance);
  const baseSlope = clamp(
    estimateCodeAnchorSlope(clusters, tokenSlope),
    -MAX_ROW_SLOPE,
    MAX_ROW_SLOPE,
  );

  const anchors = clusters
    .map((cluster) => {
      const sortedByX = [...cluster.tokens].sort(
        (left, right) => tokenCenterX(left) - tokenCenterX(right),
      );
      const primaryToken = sortedByX[0];
      const codes = [
        ...new Set(
          sortedByX
            .flatMap((token) => extractDrugCodes(token.text))
            .filter(isNumericDrugCode),
        ),
      ];
      return {
        centerX: tokenCenterX(primaryToken),
        centerY: tokenCenterY(primaryToken),
        primaryCode: codes[0] ?? null,
        codes,
        codeTokens: sortedByX,
        slope: baseSlope,
      };
    })
    .filter((anchor) => anchor.primaryCode)
    .sort((left, right) => left.centerY - right.centerY);

  if (anchors.length < 2) return [];

  const pitchValues = [];
  for (let index = 1; index < anchors.length; index += 1) {
    pitchValues.push(
      Math.abs(anchors[index].centerY - anchors[index - 1].centerY),
    );
  }
  const medianPitch = median(pitchValues, medianTokenHeight * 1.6);
  const anchorLeftEdges = anchors
    .map((anchor) => anchor.codeTokens[0]?.minX)
    .filter(Number.isFinite);
  const tableLeft = Math.max(
    0,
    percentile(
      anchorLeftEdges,
      0.1,
      Math.min(...codeTokens.map((token) => token.minX)),
    ) -
      medianTokenHeight * 0.35,
  );
  const assignedBodyTokens = [];

  const lineY = (anchor, x) =>
    anchor.centerY + (anchor.slope ?? baseSlope) * (x - anchor.centerX);
  const rowBoundaryContains = (anchorIndex, token) => {
    const centerX = tokenCenterX(token);
    const centerY = tokenCenterY(token);
    const currentY = lineY(anchors[anchorIndex], centerX);
    const previous = anchors[anchorIndex - 1];
    const next = anchors[anchorIndex + 1];
    const top = previous
      ? (lineY(previous, centerX) + currentY) / 2
      : currentY - medianPitch / 2;
    const bottom = next
      ? (currentY + lineY(next, centerX)) / 2
      : currentY + medianPitch / 2;
    const margin = Math.min(0.004, medianPitch * 0.25);
    return centerY >= top - margin && centerY <= bottom + margin;
  };

  const assigned = anchors.map(() => []);
  tokens.forEach((token) => {
    const centerY = tokenCenterY(token);
    if (
      centerY < DATA_ROW_TOP_CUTOFF - medianPitch ||
      centerY > DATA_ROW_BOTTOM_CUTOFF
    )
      return;

    let bestIndex = -1;
    let bestDistance = Infinity;
    anchors.forEach((anchor, index) => {
      const distance = Math.abs(centerY - lineY(anchor, tokenCenterX(token)));
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    if (bestIndex < 0 || !rowBoundaryContains(bestIndex, token)) return;
    assigned[bestIndex].push(token);
    assignedBodyTokens.push(token);
  });

  const rightCandidates = assignedBodyTokens.filter(
    (token) => tokenCenterX(token) > CODE_ANCHOR_MAX_X,
  );
  const tableRightSource = rightCandidates.length
    ? rightCandidates
    : assignedBodyTokens;
  const tableRight = Math.min(
    1,
    Math.max(...tableRightSource.map((token) => token.maxX), 0.94) +
      medianTokenHeight * 0.35,
  );

  const preparedRows = anchors.map((anchor, index) => {
    const rowTokens = uniqueByReference([
      ...assigned[index],
      ...anchor.codeTokens,
    ]).sort((left, right) =>
      left.minX === right.minX
        ? left.minY - right.minY
        : left.minX - right.minX,
    );
    const rowLine = estimateTokenCenterLine(rowTokens, anchor, baseSlope);
    return {
      anchor: {
        ...anchor,
        centerX: rowLine.centerX,
        centerY: rowLine.centerY,
        slope: rowLine.slope,
      },
      rowTokens,
    };
  });

  preparedRows.forEach(({ anchor }, index) => {
    anchors[index] = anchor;
  });

  // Pass 1: per-row token prep + a noisy per-row slope estimate used only as a
  // sample for the global skew fit.
  const rowPreps = preparedRows.map(({ anchor, rowTokens }, index) => {
    const localPitch = localAnchorPitch(anchors, index, medianPitch);
    const sameLineTokens = filterSameSublineTokens(
      rowTokens,
      anchor,
      medianTokenHeight,
    );
    const geometryTokens = filterVerticalOutlierTokens(
      sameLineTokens,
      anchor,
      localPitch,
      medianTokenHeight,
    );
    // Fit sample = the CLEAN text baseline (code/name/company columns only).
    // The far-right money column (총용량/총금액) curls up off the text baseline on
    // this photographed page, so including it drags the per-row slope toward the
    // flat code→money chord — the very collapse that made bands run flat. Measure
    // the left columns, which trace the true printed line direction.
    const baselineTokens = geometryTokens.filter(
      (token) => tokenCenterX(token) <= RIGHT_EDGE_MIN_X,
    );
    const baselineSampleTokens =
      baselineTokens.length >= 4 ? baselineTokens : geometryTokens;
    const rawSlope = pickBaselineSlope(baselineSampleTokens, anchor.slope);
    const bxs = baselineSampleTokens.map(tokenCenterX).filter(Number.isFinite);
    const xSpan = bxs.length ? Math.max(...bxs) - Math.min(...bxs) : 0;
    return { anchor, rowTokens, geometryTokens, localPitch, rawSlope, xSpan };
  });

  // Fit one smooth skew(cy) = a + b*cy across the whole page. Wider rows (those
  // reaching the money column) are more reliable, so weight by token x-span.
  const skewModel = fitGlobalSkew(
    rowPreps.map((prep) => ({
      cy: prep.anchor.centerY,
      slope: prep.rawSlope,
      weight: Math.max(0.2, prep.xSpan),
    })),
  );
  const skewAt = (cy) =>
    skewModel
      ? clamp(skewModel.a + skewModel.b * cy, -MAX_ROW_SLOPE, MAX_ROW_SLOPE)
      : null;

  // Pass 2: build each band using the global skew for the tilt. The per-row
  // baseline collapses on some rows (flat band covering the row above) and
  // spikes on others (over-tilted "triangle"); the global trend fixes both. Only
  // the band's vertical position/height stays per-row (from token edges). The
  // same skew is fed to foreign-token rejection so the row's OWN money column is
  // kept instead of being stripped by a wrong slope.
  return rowPreps.map(
    ({ anchor, rowTokens, geometryTokens, localPitch }, index) => {
      const globalSlope = skewAt(anchor.centerY);
      let rowSlope;
      if (globalSlope != null) {
        rowSlope = globalSlope;
      } else {
        const perRowSlope = pickBaselineSlope(geometryTokens, anchor.slope);
        const gxs = geometryTokens.map(tokenCenterX).filter(Number.isFinite);
        const gSpan = gxs.length ? Math.max(...gxs) - Math.min(...gxs) : 0;
        rowSlope =
          gSpan >= SHORT_SPAN_X_THRESHOLD
            ? perRowSlope
            : clamp(perRowSlope, -SHORT_SPAN_MAX_SLOPE, SHORT_SPAN_MAX_SLOPE);
      }
      const cleanedTokens = rejectForeignRightTokens(
        geometryTokens,
        rowSlope,
        localPitch,
        anchor,
      );
      const geometryAnchor = { ...anchor, slope: rowSlope };
      const boundaryVertices =
        makeTokenEnvelopeVertices({
          rowTokens: cleanedTokens,
          anchor: geometryAnchor,
          tableLeft,
          tableRight,
          localPitch,
          fallbackMedianHeight: medianTokenHeight,
        }) ??
        makeBoundaryVertices({
          anchors,
          index,
          tableLeft,
          tableRight,
          medianPitch,
        });
      const xs = boundaryVertices.map((vertex) => vertex.x);
      const ys = boundaryVertices.map((vertex) => vertex.y);

      return {
        centerY: anchor.centerY,
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
        text: rowTokens.map((token) => token.text).join(' '),
        primaryCode: anchor.primaryCode,
        tokenVertices: cleanedTokens,
        estimatedVertices: boundaryVertices,
      };
    },
  );
}
function extractDataRows(tokenRows) {
  // 처방전 데이터 행 기준: 한글 약품명 + 8~9자리 약품코드 포함, 상하단 여백 제외
  // centerY 상단 cutoff를 0.05로 낮춰 페이지 상단 약품 행도 포함한다.
  // (Image #7의 일부 약품 행이 centerY 0.077로 기존 0.13 cutoff에 걸려 제외됐음)
  const bodyRows = tokenRows.filter((r) => {
    if (r.centerY < DATA_ROW_TOP_CUTOFF || r.centerY > DATA_ROW_BOTTOM_CUTOFF)
      return false;
    if (HEADER_KEYWORDS.test(r.text)) return false;
    return KOREAN_RE.test(r.text) && HAS_DRUG_CODE_RE.test(r.text);
  });

  // 화면사진 fallback: 8~9자리 숫자코드 2개 이상 (bodyRows에서 누락될 경우 보완)
  const numericRows = tokenRows.filter((r) => {
    const matches = r.text.match(NUMERIC_CODE_RE) ?? [];
    return matches.length >= 2;
  });

  // 토큰 행이 많은 클린 문서 → bodyRows 우선 (단일 코드 행도 포함)
  // 토큰 행이 적은 화면 사진 → numericRows 보완 후 bodyRows와 합산
  if (tokenRows.length > 20 && bodyRows.length > numericRows.length) {
    return bodyRows;
  }
  // numericRows에는 있지만 bodyRows에 없는 행 추가 (한글 미포함 코드 전용 행)
  if (numericRows.length >= 2) {
    const bodyRowTexts = new Set(bodyRows.map((r) => r.centerY));
    const extra = numericRows.filter((r) => !bodyRowTexts.has(r.centerY));
    return [...bodyRows, ...extra].sort((a, b) => a.centerY - b.centerY);
  }
  return bodyRows;
}

function buildAssignedDataRows(drugs, dataRows) {
  if (!drugs.length || !dataRows.length) {
    return drugs.map(() => []);
  }

  const drugCount = drugs.length;
  const dataRowCount = dataRows.length;

  if (drugCount <= dataRowCount) {
    // contiguous slice: each drug gets one or more consecutive rows
    return drugs.map((_, index) => {
      const start = Math.floor((index * dataRowCount) / drugCount);
      const rawEnd = Math.floor(((index + 1) * dataRowCount) / drugCount);
      const end = Math.max(start + 1, rawEnd);
      return dataRows
        .slice(start, Math.min(end, dataRowCount))
        .map((row, offset) => ({
          row,
          sourceRowIndex: start + offset,
        }));
    });
  }

  // drugCount > dataRowCount: Bresenham-style — each drug maps to exactly one row,
  // spread as evenly as possible so adjacent drugs get distinct rows when available.
  return drugs.map((_, index) => {
    const rowIndex = Math.round((index * (dataRowCount - 1)) / (drugCount - 1));
    return [{ row: dataRows[rowIndex], sourceRowIndex: rowIndex }];
  });
}

function pickClosestIndex(indices, targetIndex) {
  return [...indices].sort((left, right) => {
    const leftDistance = Math.abs(left - targetIndex);
    const rightDistance = Math.abs(right - targetIndex);
    if (leftDistance !== rightDistance) return leftDistance - rightDistance;
    return left - right;
  })[0];
}

// ── drug code 기반 row 매칭 (안전한 leading-code override만 적용) ───────
// dataRow 수가 drug 수보다 적을 때 positional mapping은 인접 row가 한 칸씩
// 밀릴 수 있다. 이때 dataRow의 첫 코드(primary code)와 drugCode가 정확히
// 맞아떨어지는 경우에만 positional 결과를 덮어쓴다.
function buildAssignedDataRowsByCodeMatch(drugs, dataRows) {
  if (!drugs.length || !dataRows.length) {
    return drugs.map(() => []);
  }

  const positionalRows = buildAssignedDataRows(drugs, dataRows);

  const primaryCodeToRowIndices = new Map();
  dataRows.forEach((row, idx) => {
    const primaryCode = row.primaryCode ?? extractDrugCodes(row.text ?? '')[0];
    if (!primaryCode) return;
    const indices = primaryCodeToRowIndices.get(primaryCode) ?? [];
    indices.push(idx);
    primaryCodeToRowIndices.set(primaryCode, indices);
  });

  return drugs.map((drug, index) => {
    const code = String(drug.drugCode ?? '').trim();
    if (!code) return positionalRows[index];

    const exactRowIndices = primaryCodeToRowIndices.get(code);
    if (!exactRowIndices?.length) return positionalRows[index];

    const targetRowIndex =
      positionalRows[index]?.[0]?.sourceRowIndex ??
      Math.round(
        (index * (dataRows.length - 1)) / Math.max(drugs.length - 1, 1),
      );
    const rowIndex = pickClosestIndex(exactRowIndices, targetRowIndex);
    return [{ row: dataRows[rowIndex], sourceRowIndex: rowIndex }];
  });
}

function buildRowPolygons(assignedRows) {
  if (!assignedRows.length) return [];
  return assignedRows
    .map(({ row }) => rowToTiltedVertices(row))
    .filter(Boolean);
}

// ── row → 기울기 반영 4점 parallelogram ──────────────────────────────────
// 왼쪽 끝 토큰의 TL/BL + 같은 라인의 오른쪽 끝 토큰의 TR/BR 사용
// 처방전 행이 두 서브라인(코드 + 이름 wrap)으로 분리될 경우
// 왼쪽 토큰(항상 1번 서브라인)의 centerY를 기준으로 같은 라인만 필터링
function rowToTiltedVertices(row) {
  if (
    Array.isArray(row.estimatedVertices) &&
    row.estimatedVertices.length >= 4
  ) {
    return row.estimatedVertices;
  }

  const tv = row.tokenVertices;
  if (!tv || tv.length === 0) {
    const { minX, maxX, minY, maxY } = row;
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
  }

  // 토큰 높이 중앙값 (1글자 높이 추정)
  const heights = tv.map((t) => t.maxY - t.minY).sort((a, b) => a - b);
  const medianH = heights[Math.floor(heights.length / 2)] || 0.012;

  // 왼쪽 끝 토큰: 처방 코드 열 → 항상 1번 서브라인에 있음
  const leftToken = tv.reduce((a, b) => (a.minX <= b.minX ? a : b));
  const leftCY = (leftToken.minY + leftToken.maxY) / 2;

  // 같은 라인 필터: leftToken의 centerY ± SUBLINE_TOLERANCE_RATIO× 글자높이 이내 토큰만
  // (처방전 기울기 전체 offset이 ~0.012 수준이므로 이 범위면 충분)
  const sameLine = tv.filter((t) => {
    const cy = (t.minY + t.maxY) / 2;
    return Math.abs(cy - leftCY) <= medianH * SUBLINE_TOLERANCE_RATIO;
  });
  const pool = sameLine.length >= 2 ? sameLine : tv;

  const rightToken = pool.reduce((a, b) => (a.maxX >= b.maxX ? a : b));

  const tl = leftToken.ordered[0];
  const bl = leftToken.ordered[3];
  const tr = rightToken.ordered[1];
  const br = rightToken.ordered[2];

  // 디버그: 처음 20개 약품 행에 대해 실제 꼭짓점 출력
  const debugKey = `${row.centerY?.toFixed(4)}`;
  if (!rowToTiltedVertices._logged) rowToTiltedVertices._logged = new Set();
  if (
    !rowToTiltedVertices._logged.has(debugKey) &&
    rowToTiltedVertices._logged.size < 20
  ) {
    rowToTiltedVertices._logged.add(debugKey);
    const fmt = (v) => (v ? `(${v.x.toFixed(4)},${v.y.toFixed(4)})` : '?');
    const filt =
      sameLine.length < tv.length
        ? ` [filtered ${tv.length}→${sameLine.length}]`
        : '';
    console.log(
      `[VERT] centerY=${debugKey} medH=${medianH.toFixed(4)}${filt}` +
        ` TL=${fmt(tl)} TR=${fmt(tr)} BR=${fmt(br)} BL=${fmt(bl)}`,
    );
  }

  return [tl, tr, br, bl];
}

// ── 좌표 파이프라인 (재사용) ───────────────────────────────────────────────
// Gemini drugs + Document AI raw document → 하이라이트 row 계산. 동기 경로
// (/api/ocr/analyze)와 비동기 배치 경로(/api/ocr/batch/geometry)가 공유한다.
// document 는 `/document-ai/process-upload/raw` 또는 배치 raw 엔드포인트 응답의
// `{ text, pages:[{ tokens, dimension }] }` 형태.
// Merged 제약사 cells: the company label is printed once and vertically spans a
// block of drug rows, so OCR (Document AI gives no merge structure; Gemini
// usually emits the company only on the first drug of the block) leaves the rest
// blank → they fall into '미분류'. Reconstruct the merge by forward-filling the
// company down each contiguous block in reading order. Self-gating: a doc with NO
// company column has every row blank → nothing to fill (no-op, BQ recovery still
// runs); a fully-filled doc is also a no-op. Only mixed (merged) docs get filled.
// Caveat: if Gemini attaches a centred label to a MIDDLE drug, rows above it get
// the previous company — upgrade to Document-AI company-token Y-spans if seen.
function fillMergedCompanyNames(rows) {
  let lastCompany = null;
  for (const row of rows) {
    const raw =
      typeof row.rawPharmaCompanyName === 'string'
        ? row.rawPharmaCompanyName.trim()
        : row.rawPharmaCompanyName;
    if (raw) {
      lastCompany = raw;
      row.rawPharmaCompanyName = raw;
    } else if (lastCompany) {
      row.rawPharmaCompanyName = lastCompany;
      row.pharmaCompanyInferred = true;
    }
  }
  return rows;
}

export function buildAnalysisRows({
  drugs = [],
  document = {},
  requestedYearMonth = null,
  prescriptionYearMonth = null,
}) {
  const doc = document ?? {};
  const fullText = doc.text ?? '';
  const page = doc.pages?.[0] ?? {};
  const tokens = page.tokens ?? [];
  const dimension = page.dimension ?? { width: 1, height: 1 };

  const tokenRows = groupTokensByRow(tokens, fullText);
  const validDrugCodes = new Set(
    drugs
      .map((drug) => String(drug.drugCode ?? '').trim())
      .filter((code) => isNumericDrugCode(code)),
  );
  const anchoredDataRows = buildCodeAnchoredDataRows(tokenRows, validDrugCodes);
  const fallbackDataRows = extractDataRows(tokenRows);
  const dataRows =
    anchoredDataRows.length >=
    Math.max(2, Math.floor(fallbackDataRows.length * 0.8))
      ? anchoredDataRows
      : fallbackDataRows;
  const assignedDataRows = buildAssignedDataRowsByCodeMatch(drugs, dataRows);

  const geometrySourceRecords = [];
  const rows = drugs.map((drug, idx) => {
    const mappedRows = assignedDataRows[idx] ?? [];
    const rowId = `row-${idx + 1}`;

    const normalizedPolygons = mappedRows.length
      ? buildRowPolygons(mappedRows)
      : [];
    const unmatched = normalizedPolygons.length === 0;
    const normalizedVertices = normalizedPolygons[0] ?? null;

    geometrySourceRecords.push({
      rowId,
      sourceRows: mappedRows.map(({ row }) => row),
    });

    return {
      rowId,
      rawDrugName: drug.drugName ?? null,
      rawDrugCode: drug.drugCode ?? null,
      rawPharmaCompanyName: drug.pharmaCompanyName ?? null,
      rawUnitPrice: drug.unitPrice ?? null,
      rawUsageAmount: drug.outQuantity ?? drug.inQuantity ?? null,
      rawClaimAmount: drug.totalAmount ?? null,
      rawInHouseUsageAmount: drug.inQuantity ?? null,
      normalizedVertices,
      normalizedPolygons,
      unmatched,
      sourceRowIds: mappedRows.map(
        ({ sourceRowIndex }) => `source-row-${sourceRowIndex + 1}`,
      ),
      sourceRowCount: mappedRows.length,
    };
  });

  // Reconstruct merged 제약사 cells (company spanning multiple rows) unless
  // explicitly disabled. Mutates rawPharmaCompanyName in reading order.
  if (!/^(0|false|off|no)$/i.test(process.env.OCR_FILL_MERGED_COMPANY ?? '1')) {
    fillMergedCompanyNames(rows);
  }

  const unmatchedCount = rows.filter((row) => row.unmatched).length;
  const geometryDiagnostics = buildHighlightGeometryDiagnostics(
    rows,
    geometrySourceRecords,
  );

  return {
    rows,
    dimension,
    geometryDiagnostics,
    unmatchedCount,
    dataRowCount: dataRows.length,
    tokenRowCount: tokenRows.length,
    effectivePrescriptionYearMonth:
      requestedYearMonth ?? prescriptionYearMonth ?? null,
  };
}
